import { Plus, X } from "lucide-react";
import { useMemo, useState } from "react";

import { useToast } from "@/app/providers/ToastProvider";
import { CategoryGroups, groupByCategory } from "@/components/common/CategoryGroups";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { EmptyState, ErrorState } from "@/components/common/EmptyState";
import { FloatingButton } from "@/components/common/FloatingButton";
import { Loader } from "@/components/common/Loader";
import { SearchInput } from "@/components/common/SearchInput";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { FALLBACK_CATEGORY_CODE } from "@/features/categories/api/categoriesApi";
import { useProvisionCategories } from "@/features/categories/hooks/useCategories";
import { ProvisionList } from "@/features/provisions/components/ProvisionList";
import { useDeleteProvision, useProvisions } from "@/features/provisions/hooks/useProvisions";
import ProvisionFormPage from "@/features/provisions/ProvisionFormPage";
import type { Provision } from "@/features/provisions/types";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { toErrorMessage } from "@/lib/swr";

export default function ProvisionsPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [editing, setEditing] = useState<Provision | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Provision | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const isDesktop = useIsDesktop();
  const toast = useToast();

  const { provisions, isLoading, error, refresh } = useProvisions();
  const { categories } = useProvisionCategories();
  const { remove, isDeleting } = useDeleteProvision();

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return provisions;
    return provisions.filter(
      (provision) => provision.name.toLowerCase().includes(term) || provision.description?.toLowerCase().includes(term),
    );
  }, [provisions, searchTerm]);

  /** Categories replace paging here: they cut the catalogue into short lists. */
  const groups = useMemo(() => groupByCategory(filtered, categories, FALLBACK_CATEGORY_CODE), [filtered, categories]);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const name = pendingDelete.name;
    setDeleteError(null);
    try {
      await remove(pendingDelete.id);
      toast.success(`« ${name} » supprimée.`);
      setPendingDelete(null);
    } catch (e) {
      // A 409 names the recipes still using it, which is the useful part.
      const message = toErrorMessage(e) ?? "Le serveur n’a pas précisé la cause.";
      setDeleteError(`Suppression impossible. ${message} Vérifiez la cause puis réessayez.`);
    }
  };

  if (isCreating || editing) {
    return (
      <ProvisionFormPage
        provision={editing ?? undefined}
        onBack={() => {
          setIsCreating(false);
          setEditing(null);
          refresh();
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Matières premières"
        subtitle="Le catalogue de ce que vous achetez pour produire"
        action={
          isDesktop ? (
            <Button type="button" onClick={() => setIsCreating(true)} size="lg">
              <Plus />
              Nouvelle matière
            </Button>
          ) : null
        }
      />

      <SearchInput
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Rechercher une matière…"
      />

      {isLoading && <Loader />}

      {!isLoading && error && <ErrorState message={error} onRetry={() => refresh()} />}

      {!isLoading && !error && filtered.length === 0 && (
        <EmptyState
          title={searchTerm ? "Aucune matière ne correspond" : "Aucune matière première"}
          hint={
            searchTerm ? "Essayez un autre nom." : "Ajoutez ce que vous achetez pour produire : riz, huile, poulet…"
          }
          action={
            searchTerm ? (
              <Button type="button" variant="outline" onClick={() => setSearchTerm("")}>
                <X />
                Effacer la recherche
              </Button>
            ) : (
              <Button type="button" onClick={() => setIsCreating(true)}>
                <Plus />
                Nouvelle matière
              </Button>
            )
          }
        />
      )}

      {!isLoading && !error && filtered.length > 0 && (
        <CategoryGroups groups={groups} countLabel="matière">
          {(items) => <ProvisionList provisions={items} onEdit={setEditing} onDelete={setPendingDelete} />}
        </CategoryGroups>
      )}

      {!isDesktop && <FloatingButton icon={Plus} ariaLabel="Nouvelle matière" onClick={() => setIsCreating(true)} />}

      {pendingDelete && (
        <ConfirmDialog
          title={`Supprimer « ${pendingDelete.name} » ?`}
          description="Elle disparaîtra du catalogue et de vos stocks provisionnels."
          details="Les achats déjà enregistrés sont conservés comme trace. Si une recette active l’utilise encore, la suppression sera refusée."
          confirmLabel="Supprimer"
          isPending={isDeleting}
          error={deleteError}
          onConfirm={handleDelete}
          onCancel={() => {
            setDeleteError(null);
            setPendingDelete(null);
          }}
        />
      )}
    </div>
  );
}
